import React, { useState, useEffect } from 'react';
import loadable from '@loadable/component';
import { useSelector } from 'react-redux';
import { WindowLocation } from '@reach/router';
import { Link } from 'gatsby';
import Banner from '@ecri/react-components/MemberPageBanner';
import Layout from '../layout';
import ContactUsBox from '../components/ContactUsBox';
import MyContentEditor from '../components/MyContentEditor';
import InfoRequest from '../components/InfoRequest';
import SuggestedTopics from '../components/SuggestedTopics';
import { TabHeader, TabSelector, TabBody } from '../components/Tabs';
import LoadingComponent from '../components/LoadingComponent';
import { AppState } from '../store';
import { usageLogIds, LoginSource } from '../util/config';
import { useProtectPage } from '../hooks';
import styles from './myecri.module.scss';
import { loadSpace } from '@usersnap/browser';

const PasswordReset = loadable(() => import('../components/PasswordChange'));
const SavedItems = loadable(() => import('../components/SavedItems'), {
  fallback: <LoadingComponent />,
});
const SubscriptionExpirationNotice = loadable(
  () => import('../components/SubscriptionExpirationNotice'),
  {
    fallback: <LoadingComponent />,
  }
);
const ContactInfoEditor = loadable(() =>
  import('../components/ContactInfoEditor')
);

type MyEcriProps = { location: WindowLocation };

enum MyEcriTabs {
  MyContent,
  SavedItems,
  MyAccount,
}

const MyEcriPage: React.FC<MyEcriProps> = ({ location }) => {
  const [selectedTabType, setSelectedTab] = useState(MyEcriTabs.MyContent);
  const user: any = useSelector((state: AppState) => state.auth.user);
  const [loggedIn, setUser] = useProtectPage(user);

  useEffect(() => {
    setUser(user);
  }, [user]);

  useEffect(() => {
    loadSpace(`${process.env.GATSBY_USERSNAP_GLOBAL_NPS_API_KEY}`).then((api) => {
        api.init({
          user: {email: user?.profile?.email}
        })
    })
}, [])

  useEffect(() => {
    const { hash = '' } = location;
    if (hash !== '') {
      switch (hash) {
        case '#tabs-1':
          setSelectedTab(MyEcriTabs.MyContent);
          break;
        case '#tabs-2':
          setSelectedTab(MyEcriTabs.SavedItems);
          break;
        case '#tabs-3':
          setSelectedTab(MyEcriTabs.MyAccount);
          break;
        default:
          break;
        //do nothing
      }
    }
  }, [location]);

  if (!loggedIn) {
    return (
      <Layout analyticsSiteId={usageLogIds.myecri} title="My ECRI">
        <Banner fullWidth />
      </Layout>
    );
  }

  const showContactEditor =
    (user?.profile?.login_method ?? '') == LoginSource.UserNameAndPassword ||
    (user?.profile?.login_method ?? '') == LoginSource.FederatedSso;

  return (
    <Layout analyticsSiteId={usageLogIds.myecri} title="My ECRI">
      <Banner fullWidth>
        <div className={`row ${styles.myEcriHeader}`}>
          <div className="col-md-8">
            {user &&
              (user?.profile?.given_name ?? '') !== '' &&
              (user?.profile?.family_name ?? '') !== '' && (
                <h3>
                  Welcome, {user.profile.given_name} {user.profile.family_name}
                </h3>
              )}
            <p>
              My ECRI is your personalized homepage. Navigate to your
              Applications, Publications, Resources and Services, and see new
              and updated content from the topics you follow. Manage your saved
              searches, topics and documents. Administer all aspects of your
              account. Make requests for custom content, contact client
              services, and more.{' '}
              <Link to={'/your-membership/'}>Memberships & Services &gt;</Link>
            </p>
          </div>
        </div>
      </Banner>

      <div className="container pagecontent">
        <div className="row fluid">
          <article className="col-sm-12 col-md-8">
            <TabHeader>
              <TabSelector
                title="MY CONTENT"
                selected={selectedTabType === MyEcriTabs.MyContent}
                onSelected={() => {
                  if (selectedTabType !== MyEcriTabs.MyContent) {
                    setSelectedTab(MyEcriTabs.MyContent);
                  }
                }}
              ></TabSelector>
              <TabSelector
                title="SAVED ITEMS"
                selected={selectedTabType === MyEcriTabs.SavedItems}
                onSelected={() => {
                  if (selectedTabType !== MyEcriTabs.SavedItems) {
                    setSelectedTab(MyEcriTabs.SavedItems);
                  }
                }}
              ></TabSelector>
              <TabSelector
                title="MY ACCOUNT"
                selected={selectedTabType === MyEcriTabs.MyAccount}
                onSelected={() => {
                  if (selectedTabType !== MyEcriTabs.MyAccount) {
                    setSelectedTab(MyEcriTabs.MyAccount);
                  }
                }}
              ></TabSelector>
            </TabHeader>
            <TabBody>
              {selectedTabType === MyEcriTabs.MyContent && <MyContentEditor />}
              {selectedTabType === MyEcriTabs.SavedItems && <SavedItems />}
              {selectedTabType === MyEcriTabs.MyAccount && (
                <div className={styles.myAccountContainer}>
                  <SubscriptionExpirationNotice />
                  {(user?.profile?.login_method ?? '') ===
                    LoginSource.UserNameAndPassword && (
                    <PasswordReset
                      className={styles.myAccountSection}
                      email={user?.profile?.email ?? ''}
                      token={user?.access_token ?? ''}
                    />
                  )}
                  {showContactEditor && (
                    <ContactInfoEditor className={styles.myAccountContainer} />
                  )}
                  <div className={styles.eLearningLinkContainer}>
                    <h4>Manage Elearning Profile</h4>
                    <div>
                      <Link to="/lmsprofile" state={{ source: 'membersite' }}>
                        Click here to manage your eLearning Profile
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </TabBody>
          </article>
          <aside className="col-sm-12 col-md-4">
            <ContactUsBox />
            <InfoRequest />
            <SuggestedTopics />
          </aside>
        </div>
      </div>
    </Layout>
  );
};

export default MyEcriPage;
